import React from 'react'
import { Box, Typography } from '@mui/material'
import ConfirmDialog from './shared/dialogs/ConfirmDialog'

interface XeroReauthorizeDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  fpCode: string
  franchiseName: string
  tenantName?: string
  loading?: boolean
}

/**
 * Confirm re-authorizing a Xero connection. Thin wrapper over the shared
 * `ConfirmDialog` (warning tone + acknowledgement); prop contract is
 * unchanged so existing call sites keep working.
 */
const XeroReauthorizeDialog: React.FC<XeroReauthorizeDialogProps> = ({
  open,
  onClose,
  onConfirm,
  fpCode,
  franchiseName,
  tenantName,
  loading = false,
}) => (
  <ConfirmDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    tone="warning"
    title="Re-authorize Xero Connection"
    subtitle={`${franchiseName} · ${fpCode.toUpperCase()}`}
    pending={loading}
    confirmLabel="Re-authorize Xero"
    acknowledgement="I understand this will disconnect the current Xero connection"
    body={<XeroReauthorizeBody tenantName={tenantName} />}
  />
)

const XeroReauthorizeBody: React.FC<{ tenantName?: string }> = ({ tenantName }) => (
  <Box>
    <Typography variant="body2">
      This disconnects the current Xero connection before establishing a new one.
    </Typography>

    {tenantName && (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Current Xero organisation
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {tenantName}
        </Typography>
      </Box>
    )}

    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
      What will happen:
    </Typography>
    <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
        The current Xero connection is safely disconnected
      </Typography>
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
        All stored tokens and connection data are removed
      </Typography>
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
        You are redirected to Xero to authorise the desired organisation
      </Typography>
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
        Syncing with Xero is paused during this process
      </Typography>
    </Box>
  </Box>
)

export default XeroReauthorizeDialog
